/**
 * The state boundary — how a route reaches one of the eleven.
 *
 * A route group wraps its screens in `<SurfaceStates surface="…">`. When a state is
 * resolved the boundary renders that state's drawn frame; otherwise it renders the
 * screen, byte for byte as it was. Nothing about the healthy render changes, which is
 * what keeps the 2% visual gate on the built screens meaning what it meant.
 *
 * TWO WAYS IN, AND THEY ARE NOT INTERCHANGEABLE
 *
 * 1. `error` — an `ApiErrorBody`. This is the real path. Its `code` maps through
 *    `ERROR_CODE_STATE` to exactly one state, and its `message_bn` is the copy. Nine
 *    codes, nine states, a bijection.
 *
 * 2. `?state=` — a URL seam, present ONLY in a build that cannot reach the real API.
 *    It is how the visual gate captures the eleven drawn states and it is inert in the
 *    bundle a customer loads; `stateSeamOpen()` below carries the whole argument.
 *    It can only reach the NINE states that have drawn copy. `service_paused` and
 *    `approval_pending` have no drawn frame and no drawn sentence, so they are
 *    reachable only through path 1, where the server supplies `message_bn`.
 *
 * THE MATRIX IS ENFORCED, NOT DOCUMENTED
 *
 * Both paths pass through `applies(state, surface)`. Asking the AI surface for
 * `payment_failed` — a tinted cell, "the state cannot occur on that surface" — returns
 * null and the screen renders normally. A state the design says is impossible here
 * cannot be reached from here.
 *
 * WHAT HAPPENS WHEN NOTHING RESOLVES
 *
 * The children render. There is no `default:` arm, no fallback frame, and no generic
 * message anywhere in this file — the design drew no generic-failure screen and
 * `packages/contracts` has no code that would produce one. An unrecognised value is
 * treated as no state at all, which is the honest outcome: a condition nobody can name
 * is a gap in what the backend reports, not a reason for vaguer copy
 * (docs/10 § WP-K; `errors.ts` header; `P0 States § একাদশটি অবস্থা লিখে যা বেরিয়ে এল`).
 *
 * WHEN NOBODY IS SIGNED IN, NONE OF THE ELEVEN
 *
 * A refusal for want of an identity, on a device that holds no session
 * (`api/client.ts § refusalMeansSignedOut`), renders the sign-in door —
 * `auth/SignInPrompt.tsx` — on every surface but onboarding, which is the door. It is not
 * a state and does not pass through the matrix: "you are not signed in" is not a
 * condition of any surface, it is the absence of the person the surface is for. Until
 * W1-04 it was drawn as অনুমতি নেই, headed ফাউন্ডার মোড, on a customer's own account page.
 * The same refusal while a session IS held — a write whose token expired in flight — is
 * nothing at all: the transport already renewed, and the next tap succeeds.
 *
 * EVERY FRAME GETS ITS EXITS
 *
 * `renderState` hands each frame the handlers for every control it draws: back, retry,
 * in-app navigation, the tab bar. See `StateHandlers`.
 */
import { createContext, Fragment, useCallback, useContext, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams, type NavigateFunction } from 'react-router-dom';
import { isNoIdentity, type ApiErrorBody } from '@oprappo/contracts';
import type { TabBarKey } from '@oprappo/ui';

import { refusalMeansSignedOut } from '../api/client.js';
import { SignInPrompt } from '../auth/SignInPrompt.js';
import { cachedDisplayName, isFirstRun } from '../auth/session.js';
import { NEW_USER_HOME } from './data.js';

import type { StateKey } from './keys.js';
import { ERROR_CODE_STATE, isStateKey } from './keys.js';
import type { Surface } from './matrix.js';
import { applies } from './matrix.js';
import { NewUserState, newUserVariantFor } from './NewUserState.js';
import { LoadingState } from './LoadingState.js';
import { OfflineState } from './OfflineState.js';
import { PaymentFailedState } from './PaymentFailedState.js';
import { ProviderUnavailableState } from './ProviderUnavailableState.js';
import { VerificationPendingState } from './VerificationPendingState.js';
import { SyncErrorState } from './SyncErrorState.js';
import { PermissionDeniedState } from './PermissionDeniedState.js';
import { ServicePausedState } from './ServicePausedState.js';
import { ApprovalPendingState } from './ApprovalPendingState.js';
import { DomainNotLiveState, alternativesFromError, notifyAvailableFromError } from './DomainNotLiveState.js';

/** The two states with no drawn frame. Reachable only with server copy. */
export const UNDRAWN_STATES: readonly StateKey[] = ['service_paused', 'approval_pending'];

/** The search-param name the seam reads. One name, in one place. */
export const STATE_PARAM = 'state';

/**
 * The shape of the build-time environment this file consults. Three keys, no more:
 * a type this narrow is what stops "read a flag" turning into "read anything".
 */
export interface SeamEnv {
  readonly DEV?: boolean | undefined;
  readonly VITE_API_BASE?: string | undefined;
  readonly VITE_STATE_SEAM?: string | undefined;
}

/**
 * IS `?state=` REACHABLE IN THIS BUILD?
 *
 * ── THE HOLE ────────────────────────────────────────────────────────────────
 * The seam below is a URL parameter, so anybody could type it. On the deployed site
 * `oprappo.com/account/payment?state=verification_pending` drew যাচাই অপেক্ষমাণ over a
 * real customer's payment screen, and `?state=payment_failed` drew a failed payment over
 * a checkout that had not failed — a screen that lies about money, reachable by editing
 * an address bar and shareable as a link. It is a harness seam that shipped.
 *
 * ── WHY IT CANNOT SIMPLY BE DELETED ─────────────────────────────────────────
 * Eleven rows of the visual gate are captured through exactly this parameter
 * (`route-manifest.ts § S1–S11`): each is a P0 screen drawn in one state, and its route
 * is that screen's route plus `?state=`. Deleting the seam deletes the only way the
 * eleven drawn states are measured at all, which trades a small exposure for an unguarded
 * design system. So it is CLOSED, not removed — present in the build the gate serves,
 * inert in the build a customer loads.
 *
 * ── WHAT DECIDES IT, AND WHY THAT SIGNAL ────────────────────────────────────
 * `VITE_API_BASE`. A shipped bundle has one — `.github/workflows/deploy.yml` sets
 * `https://api.oprappo.com` and `api/client.ts` says in its own words that a built bundle
 * without it "is a misconfiguration". The gate's build has none: it is served statically
 * and answered by `fixtures/api/`, over a relative `/v1`. So the rule states itself —
 * NO BUILD THAT CAN REACH THE REAL API CAN REACH THE SEAM — and it holds without a new
 * CI step, which matters because `.github/workflows/**` is not this change's to edit and
 * because a flag that has to be remembered on one build is a flag that will be forgotten
 * on the other. The failure mode is also the right way round: if a gate build ever gains
 * an API base, eleven rows go RED and somebody reads this comment. If a deploy ever loses
 * one, the app is already broken for a louder reason.
 *
 * `VITE_STATE_SEAM=on|off` overrides, for the person who needs the other combination for
 * an afternoon. `DEV` keeps `vite dev` and the unit suite working as they always have.
 */
export function stateSeamOpen(env: SeamEnv = import.meta.env as SeamEnv): boolean {
  const explicit = env.VITE_STATE_SEAM;
  if (explicit === 'on') return true;
  if (explicit === 'off') return false;
  if (env.DEV === true) return true;
  return (env.VITE_API_BASE ?? '') === '';
}

export interface ResolvedState {
  key: StateKey;
  /** Present only on the error path. */
  error?: ApiErrorBody | undefined;
}

/**
 * Which state, if any, this surface should be showing.
 *
 * @param search  `location.search`, e.g. `?state=offline`.
 * @param surface the matrix column this route group belongs to.
 * @param error   an API error body, when the data layer has one.
 * @param seamOpen whether `?state=` is reachable in this build. Defaults to
 *   `stateSeamOpen()`, which is what every caller in the app uses; it is a parameter
 *   only so a test can state the shipped environment and the gate's environment side by
 *   side without stubbing a bundler global.
 */
export function resolveScreenState(
  search: string,
  surface: Surface,
  error?: ApiErrorBody | undefined,
  seamOpen: boolean = stateSeamOpen(),
): ResolvedState | null {
  if (error !== undefined) {
    // No identity is not a refusal of anything: the sign-in door when nobody is signed in
    // (`SurfaceStates`), and nothing when somebody now is. Never অনুমতি নেই.
    if (isNoIdentity(error)) return null;
    const key = ERROR_CODE_STATE[error.error.code];
    // `key` is never undefined: ERROR_CODE_STATE is total over ErrorCode, and the
    // test suite asserts it in both directions. An unknown code cannot be typed.
    return applies(key, surface) ? { key, error } : null;
  }

  // THE SEAM, AND THE ONE LINE THAT CLOSES IT. Above this point is the error path, which
  // is how a real failure reaches a real customer and is not affected. Below it is the
  // harness path. In a shipped bundle the parameter is read by nothing at all.
  if (!seamOpen) return null;

  const raw = new URLSearchParams(search).get(STATE_PARAM);
  if (raw === null || !isStateKey(raw)) return null;
  if (UNDRAWN_STATES.includes(raw)) return null;
  return applies(raw, surface) ? { key: raw } : null;
}

/**
 * What every drawn state can do, bound once per boundary.
 *
 * ── WHY THE STATES HAD NO EXITS ─────────────────────────────────────────────────
 * Every frame has a back chevron, and most have a retry, a search door or a tab bar — and
 * until W1-04 not one of them was connected. `renderState` passed copy and nothing else,
 * so a customer who met ইন্টারনেট নেই could press আবার চেষ্টা করুন until the signal came
 * back and nothing would happen, and the back chevron on পেমেন্ট ব্যর্থ went nowhere. Rule
 * four of `P0 States § চারটি নিয়ম` — every state has at least one way out — was drawn and
 * not built. These are the ways out, and `renderState` hands the right ones to each frame.
 *
 *   back      the previous screen in this app, or home when there is none: a link opened
 *             from WhatsApp has no in-app history, and `navigate(-1)` from it would
 *             leave the app altogether
 *   retry     see `SurfaceStates` — the screen's own reload when it passed one, else the
 *             surrounding boundary remounts the screen, else the page reloads
 *   navigate  in-app navigation, for the search door, the composer, an alternative
 *   tab       the customer tab bar, where a frame draws one
 */
export interface StateHandlers {
  back: () => void;
  retry: () => void;
  navigate: (to: string) => void;
  tab: (key: TabBarKey) => void;
}

/** What a frame may know about the person and the address, beyond the error body. */
export interface StateContext {
  /** The person's own name as this device last heard it, or null. Never the drawn one. */
  nameBn: string | null;
  /** The `q` in the address — the person's own words, echoed. Never composed. */
  queryBn: string | undefined;
}

/** The one search door (docs/05 § search). */
const SEARCH_PATH = '/search';
/** The AI tab. */
const AI_PATH = '/ai';

/** The five customer tabs and where each one lives. */
const TAB_PATHS: Record<TabBarKey, string> = {
  home: '/',
  explore: '/explore',
  ai: AI_PATH,
  activity: '/activity',
  account: '/account',
};

/** A server sentence that is there to show. `''` is the server saying it has none. */
function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * Render one resolved state.
 *
 * Every arm is a named state. There is deliberately no `default`, and TypeScript's
 * exhaustiveness over `StateKey` is what keeps it that way: a twelfth state would fail
 * to compile here rather than fall into a generic screen.
 *
 * Every arm also hands its frame the handlers for every control it draws — the test in
 * `__tests__/honest.test.tsx` walks each frame this returns and presses every button.
 */
export function renderState(
  resolved: ResolvedState,
  surface: Surface,
  h: StateHandlers,
  ctx: StateContext,
): JSX.Element | null {
  const err = resolved.error;
  const message = err?.error.message_bn;

  switch (resolved.key) {
    case 'new_user': {
      const variant = newUserVariantFor(surface);
      return (
        <NewUserState
          variant={variant}
          nameBn={ctx.nameBn}
          onAction={() => h.navigate(variant === 'ai' ? AI_PATH : SEARCH_PATH)}
          onSearch={() => h.navigate(SEARCH_PATH)}
          // The drawn word, handed to the one door as the person's query: the door
          // decides what রিচার্জ means, and says so if it is not running.
          onQuickAction={(id) => {
            const label = NEW_USER_HOME.quick_actions.find((a) => a.id === id)?.label_bn;
            h.navigate(label === undefined ? SEARCH_PATH : `${SEARCH_PATH}?q=${encodeURIComponent(label)}`);
          }}
          onBack={h.back}
          onTab={h.tab}
        />
      );
    }
    case 'loading':
      return <LoadingState queryBn={ctx.queryBn} onBack={h.back} />;
    case 'offline':
      return <OfflineState onRetry={h.retry} nameBn={ctx.nameBn} />;
    case 'payment_failed':
      return (
        <PaymentFailedState
          moneyPositionBn={err?.money_position_bn}
          causeBn={message}
          reference={err?.error.reference}
          onRetry={h.retry}
          onBack={h.back}
        />
      );
    case 'provider_unavailable':
      return (
        <ProviderUnavailableState
          titleBn={err?.title_bn}
          bodyBn={message}
          onSearch={() => h.navigate(SEARCH_PATH)}
          onBack={h.back}
        />
      );
    case 'verification_pending':
      return <VerificationPendingState bodyBn={message} headerBn={err?.header_bn} onBack={h.back} />;
    case 'sync_error':
      return (
        <SyncErrorState
          messageBn={message}
          reference={err?.error.reference}
          activityChrome={surface === 'activity'}
          onRetry={h.retry}
          onTab={h.tab}
        />
      );
    case 'permission_denied':
      return (
        <PermissionDeniedState
          headerBn={err?.header_bn}
          titleBn={err?.title_bn}
          bodyBn={said(err?.body_bn) ? err?.body_bn : message}
          onBack={h.back}
        />
      );
    case 'domain_not_live':
      return (
        <DomainNotLiveState
          alternatives={alternativesFromError(err)}
          notifyAvailable={notifyAvailableFromError(err)}
          queryBn={ctx.queryBn}
          titleBn={err?.title_bn}
          bodyBn={message}
          onAlternative={(id) => h.navigate(`/offering/${encodeURIComponent(id)}`)}
          onSearch={() => h.navigate(SEARCH_PATH)}
          onBack={h.back}
        />
      );
    // The two undrawn states carry no drawn sentence, so they render only when the
    // server has sent one. With no `message_bn` there is nothing to say that would
    // not have been written here, and writing it is what docs/01 forbids.
    case 'service_paused':
      return message === undefined ? null : <ServicePausedState messageBn={message} onBack={h.back} />;
    case 'approval_pending':
      return message === undefined ? null : <ApprovalPendingState messageBn={message} onBack={h.back} />;
  }
}

/**
 * The retry of the boundary around this one, when there is one.
 *
 * Two boundaries usually stand around a screen: the route's (`withSurfaceStates`, no
 * error, the seam only) and the screen's own (`<SurfaceStates error={…}>`, inside the
 * component that called `useResource`). The frame is drawn by the INNER one, but the
 * load it has to retry lives in the component the OUTER one wraps. So the outer boundary
 * publishes a retry that remounts what it wraps — which runs that component's
 * `useResource` again from the start — and the inner one reads it here.
 */
const RetryContext = createContext<(() => void) | null>(null);

/** The customer tab a surface sits under, for the sign-in door's tab bar. */
function tabFor(surface: Surface): TabBarKey | undefined {
  if (surface === 'account') return 'account';
  if (surface === 'activity') return 'activity';
  if (surface === 'ai') return 'ai';
  return undefined;
}

export interface SurfaceStatesProps {
  surface: Surface;
  children: ReactNode;
  /** Supplied by the data layer once a route talks to the Worker. */
  error?: ApiErrorBody | undefined;
  /**
   * The screen's own reload — `useResource(…).reload` — for the frames' retry controls.
   * Absent: the surrounding boundary remounts the screen (see `RetryContext`), and with
   * no surrounding boundary the page reloads. Every retry retries.
   */
  onRetry?: (() => void) | undefined;
}

export function SurfaceStates({ surface, children, error, onRetry }: SurfaceStatesProps): JSX.Element {
  const { pathname, search } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const outer = useContext(RetryContext);
  const [attempt, setAttempt] = useState(0);
  const remount = useCallback(() => setAttempt((n) => n + 1), []);

  // NOBODY IS SIGNED IN — the door, not a state, and before the matrix is consulted:
  // it is not a condition of any surface. Never on onboarding, which IS the door.
  if (error !== undefined && surface !== 'onboarding' && refusalMeansSignedOut(error)) {
    return (
      <SignInPrompt
        returnTo={pathname + search}
        firstRun={isFirstRun()}
        onNavigate={(to) => navigate(to)}
        onBack={() => goBack(navigate)}
        tab={tabFor(surface)}
        onTab={(key) => navigate(TAB_PATHS[key])}
      />
    );
  }

  const resolved = resolveScreenState(search, surface, error);
  if (resolved === null) {
    return (
      <RetryContext.Provider value={remount}>
        <Fragment key={attempt}>{children}</Fragment>
      </RetryContext.Provider>
    );
  }

  const handlers: StateHandlers = {
    back: () => goBack(navigate),
    retry: onRetry ?? outer ?? reloadPage,
    navigate: (to) => navigate(to),
    tab: (key) => navigate(TAB_PATHS[key]),
  };
  const query = params.get('q');
  const frame = renderState(resolved, surface, handlers, {
    nameBn: cachedDisplayName(),
    queryBn: query === null || query === '' ? undefined : query,
  });
  return frame === null ? <>{children}</> : frame;
}

/** The last resort of every retry: load the page again. Nothing else is left to re-run. */
function reloadPage(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

/**
 * Back within the app, or home. React Router numbers the entries it creates in
 * `history.state.idx`; `0` is the entry the app was opened on, and there is nothing of
 * ours behind it.
 */
function goBack(navigate: NavigateFunction): void {
  const idx =
    typeof window === 'undefined'
      ? 0
      : ((window.history.state as { idx?: unknown } | null)?.idx ?? 0);
  if (typeof idx === 'number' && idx > 0) navigate(-1);
  else navigate('/');
}

/**
 * Wrap every element of a route list in one boundary. One call per route group, so a
 * group's `routes.tsx` gains one line and its screens are not touched at all.
 */
export function withSurfaceStates<T extends { element?: ReactNode }>(
  surface: Surface,
  routes: readonly T[],
): T[] {
  return routes.map((r) => ({
    ...r,
    element: <SurfaceStates surface={surface}>{r.element}</SurfaceStates>,
  }));
}
